/**
 * LiftToProposalModal
 *
 * The Lift action: converts session judgment outputs into a governance
 * proposal that enters the Catchball lane.
 *
 * Supports multi-session aggregation: the facilitator can include results
 * from any completed sessions within the same cycle, not just the current one.
 *
 * On submit, the proposal enters `catchball` status immediately — the Lift IS
 * the act of sending it to the tosser.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, ScrollText, CheckSquare, Square, ArrowRight, Star, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { usePriorityProposalStore } from '../../../stores/prioritization/priorityProposalStore';
import type { ProposalRankedItem } from '../../../types/prioritization/priorityProposal';
import { StrategicNominationBadge } from './StrategicNominationBadge';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LiftToProposalModalProps {
  /** Always open when mounted — parent controls visibility */
  isOpen: boolean;
  onClose: () => void;

  /** The cycle this proposal belongs to */
  cycleId: string;
  cycleName?: string;

  /**
   * The Catchball loop this proposal will be attached to.
   * When provided, the proposal travels with the ball — the tosser is
   * auto-resolved from the loop's creator and notified after the Lift.
   */
  loopId?: string;

  /**
   * User ID of the person who tossed the ball to this level.
   * Overrides the auto-resolved tosser from the loop when provided.
   */
  tosserId?: string;

  /** The session to pre-select (typically the current session) */
  defaultSessionId?: string;

  /** Called with the new proposal ID after successful Lift */
  onLifted?: (proposalId: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Aggregate ranked items from multiple sessions into a single ordered list.
 *
 * Strategy:
 * - Collect every item across all selected sessions.
 * - For items appearing in multiple sessions, average their ranks.
 * - Sort ascending by average rank (lower = better).
 * - Assign final sequential ranks (1, 2, 3…).
 */
function aggregateRankedItems(
  sessions: Array<{
    id: string;
    rankedItemIds: string[];
    scores: Record<string, Record<string, number>>;
    classifications?: Record<string, Record<string, string | number>>;
  }>,
  itemTitles: Map<string, string>,
  nominatedItemIds: Set<string>,
): ProposalRankedItem[] {
  // Map: itemId → { ranks: number[], scores: number[], sessionId (last) }
  const accumulator = new Map<
    string,
    { ranks: number[]; scores: number[]; sessionId: string; category?: string }
  >();

  for (const session of sessions) {
    session.rankedItemIds.forEach((itemId, index) => {
      const rank = index + 1;
      const itemScore = session.scores?.[itemId]?.total ??
        Object.entries(session.scores?.[itemId] ?? {})
          .filter(([k]) => k !== 'total')
          .reduce((sum, [, v]) => sum + (v as number), 0);

      // Derive classification category if available
      const classEntries = session.classifications?.[itemId];
      const category = classEntries
        ? Object.entries(classEntries).find(([k]) => k !== 'label')?.[1] as string | undefined
        : undefined;

      const existing = accumulator.get(itemId);
      if (existing) {
        existing.ranks.push(rank);
        if (itemScore) existing.scores.push(itemScore);
        if (category) existing.category = category;
        existing.sessionId = session.id;
      } else {
        accumulator.set(itemId, {
          ranks: [rank],
          scores: itemScore ? [itemScore] : [],
          sessionId: session.id,
          category,
        });
      }
    });
  }

  // Convert to final list, sort by average rank
  const items: ProposalRankedItem[] = Array.from(accumulator.entries()).map(
    ([itemId, data]) => {
      const avgRank =
        data.ranks.reduce((s, r) => s + r, 0) / data.ranks.length;
      const avgScore =
        data.scores.length > 0
          ? data.scores.reduce((s, v) => s + v, 0) / data.scores.length
          : undefined;
      return {
        itemId,
        itemTitle: itemTitles.get(itemId) ?? itemId,
        rank: avgRank,
        score: avgScore,
        category: data.category,
        sessionId: data.sessionId,
        isStrategicallyNominated: nominatedItemIds.has(itemId),
      };
    },
  );

  items.sort((a, b) => a.rank - b.rank);

  // Re-assign sequential integer ranks
  return items.map((item, idx) => ({ ...item, rank: idx + 1 }));
}

// ─── Session selector row ─────────────────────────────────────────────────────

function SessionRow({
  session,
  selected,
  onToggle,
}: {
  session: {
    id: string;
    name: string;
    selectedToolId?: string;
    state: string;
    rankingOutput?: { rankedItemIds?: string[] } | null;
  };
  selected: boolean;
  onToggle: () => void;
}) {
  const isComplete =
    session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED';
  // A session can hold a finished ranking and still not be finalised — the
  // RUN_COMPLETE transition is refused when the session has no criteria, which
  // is what happens when its Track declares none. Labelling that "Not yet run"
  // is false: it has run, and the ten ranked items are sitting on the row. Say
  // which of the two it is, so the reason it cannot be lifted is findable.
  const hasRanking = (session.rankingOutput?.rankedItemIds?.length ?? 0) > 0;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!isComplete}
      className={`w-full flex items-center gap-3 px-4 py-3  border text-left transition-colors ${
        selected
          ? 'border-[var(--slate)] bg-[var(--slate-dim)]'
          : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
      } ${!isComplete ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {selected ? (
        <CheckSquare className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
      ) : (
        <Square className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-pri)] truncate">{session.name}</p>
        <p className="text-xs text-[var(--text-ter)]">
          {session.selectedToolId ?? 'No tool selected'} &middot;{' '}
          {isComplete ? (
            <span className="text-[var(--green)] dark:text-[var(--text-ter)] font-medium">Results available</span>
          ) : hasRanking ? (
            <span className="text-[var(--mustard)]" title="This session has a ranking but was not marked run-complete — usually because it has no criteria.">
              Ranked, not finalised
            </span>
          ) : (
            <span className="text-[var(--text-ter)]">Not yet run</span>
          )}
        </p>
      </div>
    </button>
  );
}

// ─── Ranked item preview row ──────────────────────────────────────────────────

function RankedItemRow({
  item,
  rank,
}: {
  item: ProposalRankedItem;
  rank: number;
}) {
  return (
    <div className="flex items-center gap-3 py-2 px-3 hover:bg-[var(--surface)]">
      <span className="w-6 text-center text-sm font-bold text-[var(--text-ter)] flex-shrink-0">
        {rank}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-[var(--text-pri)] truncate">
            {item.itemTitle}
          </span>
          <ItemRelationshipFlags itemId={item.itemId} />
          {item.isStrategicallyNominated && (
            <StrategicNominationBadge variant="icon" />
          )}
          {item.category && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-[var(--hover-tint)] text-[var(--text-sec)]">
              {item.category}
            </span>
          )}
        </div>
      </div>
      {item.score !== undefined && (
        <span className="text-xs text-[var(--text-ter)] flex-shrink-0 font-mono">
          {item.score.toFixed(1)}
        </span>
      )}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export function LiftToProposalModal({
  isOpen,
  onClose,
  cycleId,
  cycleName,
  loopId,
  tosserId,
  defaultSessionId,
  onLifted,
}: LiftToProposalModalProps) {
  // ── Store access ───────────────────────────────────────────────────────

  const allSessions = useSessionStore((s) => s.sessions);
  const allItems = useItemsStore((s) => s.items);
  const userId = useUserStore((s) => s.userId) ?? 'system';
  const createProposal = usePriorityProposalStore((s) => s.createProposal);

  // ── Derived: eligible sessions in this cycle ───────────────────────────

  const cycleSessions = useMemo(
    () => allSessions.filter((s) => s.cycleId === cycleId),
    [allSessions, cycleId],
  );

  // ── Local state ────────────────────────────────────────────────────────

  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(
    () => new Set(defaultSessionId ? [defaultSessionId] : []),
  );
  const [title, setTitle] = useState('');
  const [rationale, setRationale] = useState('');
  const [openQuestions, setOpenQuestions] = useState('');
  const [step, setStep] = useState<'compose' | 'preview' | 'done'>('compose');
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setCreatedProposalId] = useState<string | null>(null);

  // ── Derived: item title map ────────────────────────────────────────────

  const itemTitleMap = useMemo(
    () => new Map(allItems.map((it) => [it.id, it.title])),
    [allItems],
  );

  // ── Derived: set of strategically nominated items ─────────────────────
  // Items where isStrategicallyNominated is set on the Item record itself.
  // (We check the nominatingCycleId match if present.)

  const nominatedItemIds = useMemo(
    () =>
      new Set(
        allItems
          .filter(
            (it) =>
              it.isStrategicallyNominated &&
              (it.nominatingCycleId === cycleId || !it.nominatingCycleId),
          )
          .map((it) => it.id),
      ),
    [allItems, cycleId],
  );

  // ── Derived: aggregated ranked items from selected sessions ───────────

  const selectedSessions = useMemo(
    () =>
      cycleSessions
        .filter((s) => selectedSessionIds.has(s.id))
        .filter(
          (s) => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED',
        )
        .map((s) => ({
          id: s.id,
          rankedItemIds: s.rankingOutput?.rankedItemIds ?? [],
          scores: s.rankingOutput?.scores ?? {},
          classifications: s.rankingOutput?.classifications,
        })),
    [cycleSessions, selectedSessionIds],
  );

  const aggregatedItems = useMemo(
    () => aggregateRankedItems(selectedSessions, itemTitleMap, nominatedItemIds),
    [selectedSessions, itemTitleMap, nominatedItemIds],
  );

  // ── Handlers ───────────────────────────────────────────────────────────

  const toggleSession = useCallback((sessionId: string) => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  }, []);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!title.trim() || !rationale.trim() || aggregatedItems.length === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const selectedSessionObjs = cycleSessions.filter((s) =>
        selectedSessionIds.has(s.id),
      );
      const trackIds = Array.from(
        new Set(
          selectedSessionObjs
            .map((s) => s.trackId)
            .filter((id): id is string => !!id),
        ),
      );

      // §2.3a: createProposal now calls lift_priority_proposal RPC — awaited,
      // canonical DB state returned, no optimistic update.
      const proposalId = await createProposal({
        cycleId,
        loopId,
        trackIds,
        sessionIds: Array.from(selectedSessionIds),
        title: title.trim(),
        rationale: rationale.trim(),
        openQuestions: openQuestions.trim(),
        rankedItems: aggregatedItems,
        createdBy: userId,
        holderId: userId,
        tosserId,
      });

      setCreatedProposalId(proposalId);
      setStep('done');
      onLifted?.(proposalId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred. Please try again.';
      setSubmitError(msg);
      console.error('[LiftToProposalModal] Lift failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    title.trim().length > 0 &&
    rationale.trim().length > 0 &&
    selectedSessionIds.size > 0 &&
    aggregatedItems.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative min-h-full flex items-start justify-center py-12 px-4">
        <div className="relative bg-[var(--bg-raised)] shadow-2xl w-full max-w-2xl overflow-hidden">

          {/* ── Header ────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--rule)]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[var(--slate-dim)] flex items-center justify-center">
                <ScrollText className="h-4 w-4 text-[var(--text-ter)]" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-[var(--text-pri)]">
                  Lift to Proposal
                </h2>
                <p className="text-xs text-[var(--text-ter)]">
                  {loopId
                    ? `Sending with the ball · ${cycleName ?? 'Catchball loop'}`
                    : cycleName
                      ? `Sending to governance · ${cycleName}`
                      : 'Sending session results to the governance lane'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 hover:bg-[var(--hover-tint)] transition-colors"
            >
              <X className="h-4 w-4 text-[var(--text-ter)]" />
            </button>
          </div>

          {/* ── Done state ────────────────────────────────────────────── */}
          {step === 'done' && (
            <div className="px-6 py-12 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--green-dim)] flex items-center justify-center mx-auto mb-4">
                <ScrollText className="h-6 w-6 text-[var(--green)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2">
                Proposal sent to Catchball
              </h3>
              <p className="text-sm text-[var(--text-ter)] max-w-sm mx-auto mb-6">
                Your priority list has entered the governance lane.{' '}
                {aggregatedItems.length} items ranked across{' '}
                {selectedSessionIds.size} session
                {selectedSessionIds.size !== 1 ? 's' : ''}.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  Close
                </button>
                <Link
                  to="/prioritization/governance/proposals"
                  onClick={onClose}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
                >
                  View Proposals
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          )}

          {/* ── Compose state ─────────────────────────────────────────── */}
          {step === 'compose' && (
            <div className="divide-y divide-[var(--rule)]">

              {/* Sessions */}
              <div className="px-6 py-5">
                <h3 className="text-sm font-semibold text-[var(--text-sec)] mb-1">
                  Contributing Sessions
                </h3>
                <p className="text-xs text-[var(--text-ter)] mb-3">
                  Select one or more completed sessions to aggregate into this proposal.
                </p>

                {cycleSessions.length === 0 ? (
                  <p className="text-sm text-[var(--text-ter)] text-center py-4">
                    No sessions found for this cycle.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {cycleSessions.map((s) => (
                      <SessionRow
                        key={s.id}
                        session={s}
                        selected={selectedSessionIds.has(s.id)}
                        onToggle={() => toggleSession(s.id)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Aggregated preview */}
              {aggregatedItems.length > 0 && (
                <div className="px-6 py-5">
                  <button
                    type="button"
                    onClick={() => setPreviewExpanded((v) => !v)}
                    className="w-full flex items-center justify-between mb-3 group"
                  >
                    <h3 className="text-sm font-semibold text-[var(--text-sec)]">
                      Aggregated Ranking Preview
                      <span className="ml-2 text-xs font-normal text-[var(--text-ter)]">
                        {aggregatedItems.length} items
                      </span>
                    </h3>
                    {previewExpanded ? (
                      <ChevronUp className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-sec)]" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-sec)]" />
                    )}
                  </button>

                  {previewExpanded && (
                    <div className="border border-[var(--rule)] bg-[var(--surface)] overflow-hidden max-h-64 overflow-y-auto">
                      <div className="divide-y divide-[var(--rule)]">
                        {aggregatedItems.map((item) => (
                          <RankedItemRow
                            key={item.itemId}
                            item={item}
                            rank={item.rank}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {aggregatedItems.some((i) => i.isStrategicallyNominated) && (
                    <p className="flex items-center gap-1.5 mt-2 text-xs text-[var(--mustard)]">
                      <Star className="h-3 w-3 fill-amber-400 stroke-amber-500 flex-shrink-0" />
                      Items marked{' '}
                      <StrategicNominationBadge variant="icon" /> were seeded by
                      the executive who tossed the ball.
                    </p>
                  )}
                </div>
              )}

              {/* Proposal narrative */}
              <div className="px-6 py-5 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-[var(--text-sec)] mb-1.5">
                    Proposal Title
                    <span className="ml-1 text-[var(--coral)]">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Q3 Product Priority Proposal"
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[var(--text-sec)] mb-1.5">
                    Rationale
                    <span className="ml-1 text-[var(--coral)]">*</span>
                  </label>
                  <p className="text-xs text-[var(--text-ter)] mb-2">
                    Why does this ordering make sense? What tradeoffs did the team consider?
                  </p>
                  <textarea
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    placeholder="The team evaluated all items using the RICE framework, weighting reach and impact most heavily given Q3 growth objectives..."
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[var(--text-sec)] mb-1.5">
                    Open Questions for Catchball
                  </label>
                  <p className="text-xs text-[var(--text-ter)] mb-2">
                    What would you like feedback on? What are you uncertain about?
                  </p>
                  <textarea
                    value={openQuestions}
                    onChange={(e) => setOpenQuestions(e.target.value)}
                    placeholder="We are uncertain about the relative priority of items 3 and 4 — they scored closely. We'd welcome input on strategic fit..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-[var(--surface)] flex items-center justify-between gap-3">
                {submitError ? (
                  <p className="text-xs text-[var(--coral)] max-w-xs font-medium">
                    {submitError}
                  </p>
                ) : (
                  <p className="text-xs text-[var(--text-ter)] max-w-xs">
                    Lifting sends this proposal to Catchball immediately — the person
                    who tossed the ball will be notified.
                  </p>
                )}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit || isSubmitting}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Lifting…
                      </>
                    ) : (
                      <>
                        <ScrollText className="h-4 w-4" />
                        Lift to Proposal
                      </>
                    )}
                  </button>
                </div>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}
